/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isSameDay } from '@features/agenda/utils/agenda-dates';
import { eventColor } from './event-color';
import {
  DAY_START_HOUR,
  MIN_CHIP_HEIGHT_DAY,
  eventPlacement,
  nowOffset,
  splitAllDay,
  timeRange,
} from './time-grid';
import { ALL_DAY_SCROLL, AllDayChip, AllDayLabel } from './AllDayBand';
import { computeEventLayouts } from './event-layout';
import { Text, UnstyledButton } from '@/shared/ui';

interface DayViewProps {
  date: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
  onSlotClick?: (date: Date) => void;
}

const SLOT_HEIGHT = 56;
const HOURS = Array.from({ length: 24 }, (_, i) => i + DAY_START_HOUR);

/**
 * Typography for event chips, applied inline.
 *
 * The shared `Text` component ships its own `whitespace-pre-wrap` and
 * font-weight classes, which beat the `truncate` utility's `nowrap` and
 * `font-bold` at equal specificity — text wrapped onto extra lines, clipping
 * the time off short chips, and the bold never landed. An inline style is the
 * one thing that outranks both.
 */
const NOWRAP = { whiteSpace: 'nowrap', fontWeight: 700 } as const;

/**
 * Single-day vertical timeline with positioned events.
 *
 * @param props Date, events, calendars and select handler.
 * @returns Rendered day view.
 */
export const DayView: React.FC<DayViewProps> = ({
  date,
  events,
  calendars,
  onSelect,
  onSlotClick,
}) => {
  // All-day events get their own band pinned above the grid — parking them at
  // 00:00 meant scrolling the whole day just to read them.
  const { allDay, timed: dayEvents } = splitAllDay(
    events.filter((e) => isSameDay(e.start_at_utc, date))
  );
  const { t } = useTranslation('features/agenda');
  const now = nowOffset(date, SLOT_HEIGHT);
  const layouts = computeEventLayouts(dayEvents);
  const LEFT_OFFSET = 62;
  const RIGHT_MARGIN = 8;
  const GUTTER = 2;
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (now !== null && scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(0, now - scrollRef.current.clientHeight / 3);
    }
  }, [now]);

  return (
    <div
      data-testid="agenda-day-view"
      className="flex min-h-0 flex-1 overflow-hidden bg-[var(--surface-primary)]"
    >
      <div ref={scrollRef} className="max-h-full flex-1 overflow-x-hidden overflow-y-auto">
        {allDay.length > 0 && (
          <div
            data-testid="agenda-day-all-day"
            className="sticky top-0 z-20 flex border-b border-[var(--border-subtle)] bg-[var(--surface-primary)] py-(--space-1) pr-(--space-2)"
          >
            <AllDayLabel className="w-[54px] flex-shrink-0 pr-(--space-2-5)" />
            <div className={`flex-1 pl-(--space-2) ${ALL_DAY_SCROLL}`}>
              {allDay.map((ev) => (
                <AllDayChip
                  key={`${ev.event_id}-${ev.occurrence_start}`}
                  event={ev}
                  calendars={calendars}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        )}
        {/* The hour grid is its own positioning context, so the absolutely
            placed chips and the now-line stay measured from 00:00 even with
            the all-day band above them. */}
        <div className="relative">
          {HOURS.map((h) => (
            <div
              key={h}
              className="flex"
            >
              <div
                className="w-[54px] flex-shrink-0 border-b border-[var(--border-ultra-subtle)] pr-(--space-2-5) pt-(--space-1-5) text-right text-xs text-[var(--text-tertiary)]"
                style={{ height: SLOT_HEIGHT }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
              <UnstyledButton
                type="button"
                aria-label={`Criar evento às ${String(h).padStart(2, '0')}:00`}
                onClick={() => {
                  if (!onSlotClick) return;
                  const d = new Date(date);
                  d.setHours(h, 0, 0, 0);
                  onSlotClick(d);
                }}
                className="flex-1 cursor-pointer border-b border-l border-[var(--border-ultra-subtle)] transition-colors hover:bg-[var(--seed-base)]/[0.04] focus-visible:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]/40"
                style={{ height: SLOT_HEIGHT }}
              >
                {null}
              </UnstyledButton>
            </div>
          ))}
          {now !== null && (
            <div
              data-testid="agenda-now-line"
              aria-hidden="true"
              className="pointer-events-none absolute left-[54px] right-2 z-10"
              style={{ top: now, height: 'var(--space-2)', marginTop: 'calc(-1 * var(--space-1))' }}
            >
              <Text component="span" className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--status-error)]" />
              <Text component="span" className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[var(--status-error)]" />
            </div>
          )}
          {dayEvents.map((ev) => {
            const color = eventColor(ev, calendars);
            const { top, height } = eventPlacement(ev, SLOT_HEIGHT, MIN_CHIP_HEIGHT_DAY);
            const { colIndex, totalCols } = layouts.get(
              `${ev.event_id}:${ev.occurrence_start}`
            ) ?? { colIndex: 0, totalCols: 1 };
            const availableWidth = `calc(100% - ${LEFT_OFFSET + RIGHT_MARGIN}px)`;
            const colWidth = `calc((${availableWidth}) / ${totalCols} - ${GUTTER}px)`;
            const colLeft = `calc(${LEFT_OFFSET}px + (${availableWidth}) / ${totalCols} * ${colIndex} + ${GUTTER}px)`;
            const isRecurring = Array.isArray(ev.recurrence) && ev.recurrence.length > 0;
            return (
              <UnstyledButton
                key={`${ev.event_id}-${ev.occurrence_start}`}
                type="button"
                data-testid="agenda-event-chip"
                title={`${timeRange(ev)} · ${ev.title}`}
                onClick={() => onSelect(ev)}
                className="absolute min-w-0 overflow-hidden rounded-[var(--radius-sm)] border-l-[3px] px-(--space-2-5) py-(--space-1-5) text-left transition-shadow hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)]"
                style={{
                  top,
                  height: Math.max(height, MIN_CHIP_HEIGHT_DAY),
                  left: colLeft,
                  width: colWidth,
                  background: `color-mix(in srgb, ${color} 12%, var(--surface-primary))`,
                  borderColor: color,
                }}
              >
                {/* Title over time, one line each — see WeekColumn. The chip is
                    floored at MIN_CHIP_HEIGHT_DAY, so both lines always fit. */}
                <Text component="span"
                  style={{ color, ...NOWRAP }}
                  className={`block w-full truncate text-(--text-small) leading-tight ${isRecurring ? 'pr-(--space-4)' : ''}`}
                >
                  {ev.title}
                </Text>
                <Text component="span"
                  style={{ color, ...NOWRAP }}
                  className="block w-full truncate text-xs leading-tight"
                >
                  {timeRange(ev)}
                </Text>
                {isRecurring && (
                  <Text component="span"
                    data-testid="recurrence-indicator"
                    aria-label={t('preview.recurring')}
                    className="absolute top-1 right-1 flex items-center justify-center"
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 15 15"
                      fill="none"
                      aria-hidden="true"
                      className="opacity-70"
                      style={{ color }}
                    >
                      <path
                        d="M3.5 2a.5.5 0 0 1 .5.5v1.77A5.5 5.5 0 0 1 13 7.5a.5.5 0 0 1-1 0A4.5 4.5 0 0 0 4.58 5H6.5a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5v-3a.5.5 0 0 1 .5-.5ZM2 7.5a.5.5 0 0 1 1 0A4.5 4.5 0 0 0 10.42 10H8.5a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-1 0V10.73A5.5 5.5 0 0 1 2 7.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  </Text>
                )}
              </UnstyledButton>
            );
          })}
        </div>
      </div>
    </div>
  );
};
